import React, { useEffect, useRef, useState } from 'react';
import { IoChevronBack, IoChevronForward, IoPause, IoPlay } from 'react-icons/io5';

type Props = {
  images: readonly string[];
  alts: readonly string[];
  label: string;
  fallbackAltPrefix: string;
  className?: string;
  slideClassName?: string;
  intervalMs?: number;
  perView?: { base: number; md: number };
};

export function PhotoCarousel({ images, alts, label, fallbackAltPrefix, className = '', slideClassName = '', intervalMs = 5000, perView = { base: 1, md: 2 } }: Props) {
  const n = images.length;
  const [count, setCount] = useState(perView.base);
  const [pos, setPos] = useState(perView.base);
  const [instant, setInstant] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [visible, setVisible] = useState(true);
  const [reduced, setReduced] = useState(false);
  const [announce, setAnnounce] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const touchX = useRef<number | null>(null);
  const countRef = useRef(perView.base);
  const index = n ? ((pos - count) % n + n) % n : 0;

  useEffect(() => {
    const width = window.matchMedia('(min-width: 821px)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      const next = width.matches ? perView.md : perView.base;
      if (countRef.current !== next) {
        const old = countRef.current;
        setPos(p => p - old + next);
        setCount(next);
        countRef.current = next;
        setInstant(true);
      }
      setReduced(motion.matches);
    };
    const visibility = () => setVisible(document.visibilityState !== 'hidden');
    update(); visibility();
    width.addEventListener('change', update); motion.addEventListener('change', update);
    document.addEventListener('visibilitychange', visibility);
    return () => { width.removeEventListener('change', update); motion.removeEventListener('change', update); document.removeEventListener('visibilitychange', visibility); };
  }, [perView.base, perView.md]);

  useEffect(() => {
    if (!instant) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setInstant(false)));
    return () => cancelAnimationFrame(id);
  }, [instant]);

  useEffect(() => {
    if (n < 2 || !playing || reduced || !visible || hovered || focused) return;
    const id = window.setInterval(() => { setAnnounce(false); setPos(p => p + 1); }, intervalMs);
    return () => clearInterval(id);
  }, [n, playing, reduced, visible, hovered, focused, intervalMs, timerKey]);

  const go = (delta: number) => { if (n > 1) { setAnnounce(true); setTimerKey(k => k + 1); setPos(p => reduced ? count + (((p - count + delta) % n + n) % n) : p + delta); } };
  const goTo = (i: number) => { setAnnounce(true); setTimerKey(k => k + 1); setPos(count + i); };
  const slides = n > 1 ? [...images.slice(-count), ...images, ...images.slice(0, count)] : images;

  return <div className={`ui-carousel ${className}`} role="region" aria-roledescription="carousel" aria-label={label} tabIndex={0}
    onPointerEnter={() => setHovered(true)} onPointerLeave={() => setHovered(false)}
    onFocus={() => setFocused(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocused(false); }}
    onKeyDown={e => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); go(e.key === 'ArrowLeft' ? -1 : 1); } }}>
    <div className="ui-carousel-viewport" onPointerDown={e => { touchX.current = e.pointerType === 'touch' ? e.clientX : null; }} onPointerUp={e => { if (touchX.current !== null && Math.abs(e.clientX - touchX.current) > 40) go(e.clientX < touchX.current ? 1 : -1); touchX.current = null; }}>
      <div className="ui-carousel-track" data-instant={instant || undefined} style={{ '--pos': n > 1 ? pos : 0, '--per-view': n > 1 ? count : 1 } as React.CSSProperties}
        onTransitionEnd={e => { if (e.target !== e.currentTarget) return; if (pos < count || pos >= count + n) { setInstant(true); setPos(count + ((pos - count) % n + n) % n); } }}>
        {slides.map((src, i) => {
          const real = ((i - count) % n + n) % n;
          const active = n === 1 || (i >= count && i < count + n && ((real - index + n) % n) < count);
          return <figure key={`${src}-${i}`} className="ui-carousel-slide" role="group" aria-roledescription="slide" aria-label={`${real + 1} of ${n}`} aria-hidden={!active} inert={!active}>
            <div className={`ui-media ui-media--cover ${slideClassName}`}><img src={src} alt={i < count || i >= count + n ? '' : alts[real] ?? `${fallbackAltPrefix} ${real + 1} of ${n}`} loading="lazy" decoding="async" /></div>
          </figure>;
        })}
      </div>
      {n > 1 && <><button type="button" className="ui-carousel-nav ui-carousel-nav--prev" onClick={() => go(-1)} aria-label="Previous photo"><IoChevronBack aria-hidden /></button><button type="button" className="ui-carousel-nav ui-carousel-nav--next" onClick={() => go(1)} aria-label="Next photo"><IoChevronForward aria-hidden /></button></>}
    </div>
    {n > 1 && <div className="ui-carousel-controls"><button type="button" className="ui-carousel-toggle" onClick={() => setPlaying(p => !p)} aria-label={playing ? 'Pause slideshow' : 'Play slideshow'} aria-pressed={!playing}>{playing ? <IoPause aria-hidden /> : <IoPlay aria-hidden />}</button><div className="ui-carousel-dots" role="group" aria-label="Choose photo">{images.map((src, i) => <button key={src} type="button" className={`ui-carousel-dot ${i === index ? 'is-active' : ''}`} onClick={() => goTo(i)} aria-label={`Photo ${i + 1} of ${n}`} aria-current={i === index ? 'true' : undefined} />)}</div></div>}
    <p className="ui-visually-hidden" aria-live={announce ? 'polite' : 'off'} aria-atomic="true">Photo {index + 1} of {n}</p>
  </div>;
}
